// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/chat/chatOutbox.ts
// 🔧 PURPOSE: La coda dei messaggi scritti senza rete (chat ospite).
//             Senza connessione un messaggio non parte: resta qui, con
//             l'orologio nella bolla, e parte da solo quando la rete torna
//             (GuestChatDrawer, flushQueue), uno alla volta e nell'ordine in
//             cui è stato scritto.
//
//             Sta in memoria, fuori dal componente: sopravvive alla chiusura
//             della chat e al cambio di pagina (la chat della Home e quella
//             del Soggiorno vedono la stessa coda), non alla chiusura
//             dell'app. Le bozze non si salvano su disco, e nemmeno questa.
//
//             Ogni messaggio appartiene a una prenotazione (owner): dopo un
//             nuovo accesso alla stessa prenotazione (token nuovo, stesso
//             bookingId) la coda è ancora sua; con un'altra prenotazione no.
// ============================================

import type { ChatAttachment } from '../../lib/guestApi';

export interface OutboxItem {
  /** L'id temporaneo della bolla (tmp-…): lo stesso fino alla conferma del server. */
  id: string;
  /** La prenotazione a cui appartiene (outboxOwner). */
  owner: string;
  text: string;
  /** Foto già caricate sul server: partono con il messaggio. */
  attachments?: ChatAttachment[];
  /** Le anteprime locali delle foto (object URL), finché valgono. */
  localThumbs?: string[];
  senderName: string;
  createdAt: string;
}

let items: OutboxItem[] = [];
const listeners = new Set<() => void>();

const emit = () => {
  listeners.forEach((listener) => listener());
};

/** Avvisa quando la coda cambia; restituisce la funzione che smette. */
export function subscribeOutbox(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** La coda intera, dal più vecchio al più nuovo (un array nuovo a ogni cambio). */
export function getOutbox(): OutboxItem[] {
  return items;
}

/** I messaggi in coda di una prenotazione, in ordine. */
export function outboxFor(owner: string | null): OutboxItem[] {
  if (!owner) return [];
  return items.filter((item) => item.owner === owner);
}

/**
 * Mette un messaggio in coda. `atFront`: torna in testa (un invio respinto
 * con la sessione scaduta: il server non l'ha preso, e deve partire per
 * primo). Lo stesso id non entra due volte.
 */
export function enqueueOutbox(item: OutboxItem, atFront: boolean = false): void {
  if (items.some((existing) => existing.id === item.id)) return;
  items = atFront ? [item, ...items] : [...items, item];
  emit();
}

/**
 * Prende un messaggio dalla coda per spedirlo. null se non c'è più: l'ha
 * già preso qualcun altro, e così non parte due volte.
 */
export function takeFromOutbox(id: string): OutboxItem | null {
  const found = items.find((item) => item.id === id);
  if (!found) return null;
  items = items.filter((item) => item.id !== id);
  emit();
  return found;
}

/** Il payload del JWT, senza verificarlo (serve solo a riconoscere la prenotazione). */
function jwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3 || typeof atob !== 'function') return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const parsed = JSON.parse(atob(padded));
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * La prenotazione di un token: il bookingId del JWT (resta lo stesso dopo
 * un nuovo accesso), altrimenti il token stesso.
 */
export function outboxOwner(token: string): string {
  const payload = jwtPayload(token);
  const bookingId = payload ? payload.bookingId : undefined;
  if (typeof bookingId === 'string' && bookingId) return `b:${bookingId}`;
  if (typeof bookingId === 'number') return `b:${bookingId}`;
  return `t:${token}`;
}

/** Solo per i test: la coda vuota. */
export function resetOutboxForTests(): void {
  items = [];
  emit();
}
